// Helper pour construire les URLs API avec le tenant

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fonctions utilitaires pour localStorage avec préfixe tenant
 */
const getStorageKey = (key, tenantSlug) => {
  return tenantSlug ? `${tenantSlug}_${key}` : key;
};

export const getTenantToken = () => {
  const tenantSlug = window.location.pathname.split('/')[1];
  
  // Pour le super admin, utiliser admin_token
  if (tenantSlug === 'admin') {
    return localStorage.getItem('admin_token') || localStorage.getItem('token');
  }
  
  if (!tenantSlug) {
    return localStorage.getItem('token');
  }
  
  // Pour tous les autres tenants, utiliser le préfixe
  return localStorage.getItem(getStorageKey('token', tenantSlug));
};

export const setTenantToken = (token) => {
  const tenantSlug = window.location.pathname.split('/')[1];
  if (!tenantSlug) {
    localStorage.setItem('token', token);
  } else {
    // Pour admin et tous les autres tenants, utiliser le préfixe
    localStorage.setItem(getStorageKey('token', tenantSlug), token);
  }
};

export const getTenantItem = (key) => {
  const tenantSlug = window.location.pathname.split('/')[1];
  if (!tenantSlug) {
    return localStorage.getItem(key);
  }
  // Pour admin et tous les autres tenants, utiliser le préfixe
  return localStorage.getItem(getStorageKey(key, tenantSlug));
};

export const setTenantItem = (key, value) => {
  const tenantSlug = window.location.pathname.split('/')[1];
  if (!tenantSlug) {
    localStorage.setItem(key, value);
  } else {
    // Pour admin et tous les autres tenants, utiliser le préfixe
    localStorage.setItem(getStorageKey(key, tenantSlug), value);
  }
};

/**
 * Construit une URL API avec le préfixe tenant
 * @param {string} tenantSlug - Le slug du tenant (ex: 'shefford', 'bromont')
 * @param {string} endpoint - L'endpoint API (ex: '/users', '/types-garde')
 * @returns {string} URL complète
 */
export const buildApiUrl = (tenantSlug, endpoint) => {
  // Pour super-admin
  if (tenantSlug === 'admin') {
    return `${BACKEND_URL}/api${endpoint}`;
  }
  
  // Pour les tenants
  // Retirer le / initial de l'endpoint s'il existe
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${BACKEND_URL}/api/${tenantSlug}${cleanEndpoint}`;
};

/**
 * Effectue une requête API avec gestion automatique du tenant
 * @param {string} tenantSlug - Le slug du tenant
 * @param {string} endpoint - L'endpoint API
 * @param {object} options - Options fetch (method, headers, body, etc.)
 * @returns {Promise} Résultat de la requête
 */
export const apiCall = async (tenantSlug, endpoint, options = {}) => {
  const url = buildApiUrl(tenantSlug, endpoint);
  
  // Ajouter le token d'authentification si présent (avec préfixe tenant)
  const token = getTenantToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    ...options,
    headers,
  };
  
  try {
    const response = await fetch(url, config);
    
    // Si 401, le token est invalide - nettoyer et rediriger vers login
    if (response.status === 401) {
      console.log('Token invalide ou expiré, nettoyage et redirection...');
      localStorage.clear(); // Nettoyer TOUT le localStorage
      sessionStorage.clear(); // Nettoyer aussi sessionStorage
      
      // Attendre un peu pour éviter les boucles de redirection
      setTimeout(() => {
        window.location.href = `/${tenantSlug}`;
      }, 100);
      return;
    }
    
    // Si 403, c'est un problème de permissions, pas de token - ne PAS déconnecter
    // Laisser l'appelant gérer l'erreur

    // Cas particulier : caserne désactivée
    // Cas particulier : caserne désactivée
    if (response.status === 403) {

      const errorData = await response.clone().json().catch(() => null);

      if (errorData?.detail === "Cette caserne est désactivée.") {

          localStorage.clear();
          sessionStorage.clear();

          alert("Votre caserne a été désactivée. Vous avez été déconnecté.");

          // Redirection immédiate
          window.location.replace(`/${tenantSlug}`);

          // Arrêter complètement le traitement
          throw new Error("TENANT_DISABLED");
      }

       
      // Cas particulier : compte utilisateur désactivé ou suspendu
      if (errorData?.detail === "Votre compte a été désactivé ou suspendu.") {
 
          localStorage.clear();
          sessionStorage.clear();
 
          alert("Votre compte a été désactivé ou suspendu. Vous avez été déconnecté.");
 
          // Redirection immédiate
          window.location.replace(`/${tenantSlug}`);
 
          // Arrêter complètement le traitement
          throw new Error("USER_DISABLED");
      }
    }
      
    // Si 404 sur le tenant, rediriger vers erreur
    if (response.status === 404 && response.statusText.includes('Caserne')) {
      window.location.href = '/tenant-not-found';
      return;
    }
    
    // Vérifier le content-type avant de parser en JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Si ce n'est pas du JSON (HTML, texte, etc.), créer une erreur explicite
      const text = await response.text();
      const error = new Error(`Réponse non-JSON reçue (${contentType}): ${text.substring(0, 100)}...`);
      error.status = response.status;
      throw error;
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      // Gérer les erreurs de validation Pydantic (array) et les erreurs simples (string)
      let errorMessage = 'Erreur API';
      
      if (data.detail) {
        if (Array.isArray(data.detail)) {
          // Erreur de validation Pydantic: array d'objets avec loc, msg, type
          errorMessage = data.detail.map(err => {
            const field = err.loc ? err.loc.join(' > ') : 'Champ inconnu';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof data.detail === 'string') {
          // Erreur simple: string
          errorMessage = data.detail;
        } else {
          // Autre format: convertir en string
          errorMessage = JSON.stringify(data.detail);
        }
      } else if (data.message) {
        errorMessage = data.message;
      }
      
      // Créer une erreur avec plus de contexte pour le catch
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    
    return data;
  } catch (error) {
    // Si erreur réseau ou parsing JSON
    if (error.name === 'TypeError' || error.message.includes('JSON')) {
      console.error('Erreur réseau ou parsing:', error);
    }
    throw error;
  }
};

/**
 * Helper pour les requêtes GET
 */
export const apiGet = (tenantSlug, endpoint) => {
  return apiCall(tenantSlug, endpoint, { method: 'GET' });
};

/**
 * Helper pour les requêtes POST
 */
export const apiPost = (tenantSlug, endpoint, data) => {
  return apiCall(tenantSlug, endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Helper pour les requêtes PUT
 */
export const apiPut = (tenantSlug, endpoint, data) => {
  return apiCall(tenantSlug, endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Helper pour les requêtes PATCH
 */
export const apiPatch = (tenantSlug, endpoint, data) => {
  return apiCall(tenantSlug, endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * Helper pour les requêtes DELETE
 */
/**
 * apiDelete — supporte optionnellement un body JSON (mai 2026).
 *
 * Certaines routes DELETE attendent un body (ex: suppression d'inspection qui
 * exige `{raison}` pour audit). Si `body` est fourni, on l'envoie en JSON
 * comme pour POST/PUT. Sinon, comportement legacy : DELETE sans body.
 */
export const apiDelete = (tenantSlug, endpoint, body) => {
  const options = { method: 'DELETE' };
  if (body !== undefined && body !== null) {
    options.body = JSON.stringify(body);
  }
  return apiCall(tenantSlug, endpoint, options);
};

/**
 * Télécharge un fichier depuis l'API
 * @param {string} tenantSlug - Le slug du tenant
 * @param {string} endpoint - L'endpoint API (ex: '/remplacements/export/pdf')
 * @param {string} filename - Le nom du fichier à télécharger
 */
export const downloadFile = async (tenantSlug, endpoint, filename) => {
  const url = buildApiUrl(tenantSlug, endpoint);
  const token = getTenantToken();
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    }
  });

  if (!response.ok) {
    throw new Error(`Erreur ${response.status}: ${response.statusText}`);
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export default {
  buildApiUrl,
  apiCall,
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  downloadFile,
};
